import React, { useEffect, useState, useRef } from "react";
import "./Settings.css";
import { apiFetch } from "../utils/api";

const sortByName = list => [...list].sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));

const Settings = () => {
  const [categories, setCategories] = useState([]);
  const [treatmentTypes, setTreatmentTypes] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newTreatmentTypeName, setNewTreatmentTypeName] = useState("");
  const [newExpenseTypeName, setNewExpenseTypeName] = useState("");
  const [csvUploadLoading, setCsvUploadLoading] = useState(false);
  const [csvUploadMessage, setCsvUploadMessage] = useState("");
  const [isSavingCategory, setIsSavingCategory] = useState(false);
  const categorySubmitInFlightRef = useRef(false);
  const [isSavingTreatment, setIsSavingTreatment] = useState(false);
  const treatmentSubmitInFlightRef = useRef(false);
  const [isSavingExpenseType, setIsSavingExpenseType] = useState(false);
  const expenseTypeSubmitInFlightRef = useRef(false);

  const loadMasterData = async () => {
    try {
      const [categoryRes, treatmentRes, expenseTypeRes] = await Promise.all([
        apiFetch("/api/service-categories"),
        apiFetch("/api/treatment-types"),
        apiFetch("/api/purchase/expense-types")
      ]);

      const [categoryData, treatmentData, expenseTypeData] = await Promise.all([
        categoryRes.json(),
        treatmentRes.json(),
        expenseTypeRes.json()
      ]);

      setCategories(sortByName(Array.isArray(categoryData) ? categoryData : []));
      setTreatmentTypes(sortByName(Array.isArray(treatmentData) ? treatmentData : []));
      setExpenseTypes(sortByName(Array.isArray(expenseTypeData) ? expenseTypeData : []));
    } catch (err) {
      console.error("Failed to load settings master data", err);
    }
  };

  useEffect(() => {
    loadMasterData();
  }, []);

  const addCategory = async e => {
    e.preventDefault();
    if (categorySubmitInFlightRef.current || isSavingCategory) return;
    
    categorySubmitInFlightRef.current = true;
    setIsSavingCategory(true);

    const releaseSubmitLock = () => {
      categorySubmitInFlightRef.current = false;
      setIsSavingCategory(false);
    };

    const name = newCategoryName.trim();
    if (!name) {
      releaseSubmitLock();
      return;
    }

    try {
      const res = await apiFetch("/api/service-categories", {
        method: "POST",
        body: JSON.stringify({ name })
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);

      const created = await res.json();
      setCategories(prev => sortByName([...prev, created]));
      setNewCategoryName("");
      releaseSubmitLock();
    } catch (err) {
      console.error("Failed to add category", err);
      alert("Could not add category");
      releaseSubmitLock();
    }
  };

  const addTreatmentType = async e => {
    e.preventDefault();
    if (treatmentSubmitInFlightRef.current || isSavingTreatment) return;
    
    treatmentSubmitInFlightRef.current = true;
    setIsSavingTreatment(true);

    const releaseSubmitLock = () => {
      treatmentSubmitInFlightRef.current = false;
      setIsSavingTreatment(false);
    };

    const name = newTreatmentTypeName.trim();
    if (!name) {
      releaseSubmitLock();
      return;
    }

    try {
      const res = await apiFetch("/api/treatment-types", {
        method: "POST",
        body: JSON.stringify({ name })
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);

      const created = await res.json();
      setTreatmentTypes(prev => sortByName([...prev, created]));
      setNewTreatmentTypeName("");
      releaseSubmitLock();
    } catch (err) {
      console.error("Failed to add treatment type", err);
      alert("Could not add treatment type");
      releaseSubmitLock();
    }
  };

  const addExpenseType = async e => {
    e.preventDefault();
    if (expenseTypeSubmitInFlightRef.current || isSavingExpenseType) return;

    expenseTypeSubmitInFlightRef.current = true;
    setIsSavingExpenseType(true);

    const releaseSubmitLock = () => {
      expenseTypeSubmitInFlightRef.current = false;
      setIsSavingExpenseType(false);
    };

    const name = newExpenseTypeName.trim();
    if (!name) {
      releaseSubmitLock();
      return;
    }

    try {
      const res = await apiFetch("/api/purchase/expense-types", {
        method: "POST",
        body: JSON.stringify({ name })
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);

      const created = await res.json();
      setExpenseTypes(prev => sortByName([...prev, created]));
      setNewExpenseTypeName("");
      releaseSubmitLock();
    } catch (err) {
      console.error("Failed to add expense type", err);
      alert("Could not add expense type");
      releaseSubmitLock();
    }
  };

  const deleteCategory = async item => {
    if (!window.confirm(`Delete category "${item.name}"?`)) return;

    try {
      const res = await apiFetch(`/api/service-categories/${item.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      setCategories(prev => prev.filter(row => row.id !== item.id));
    } catch (err) {
      console.error("Failed to delete category", err);
      alert("Could not delete category");
    }
  };

  const deleteTreatmentType = async item => {
    if (!window.confirm(`Delete treatment type "${item.name}"?`)) return;

    try {
      const res = await apiFetch(`/api/treatment-types/${item.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      setTreatmentTypes(prev => prev.filter(row => row.id !== item.id));
    } catch (err) {
      console.error("Failed to delete treatment type", err);
      alert("Could not delete treatment type");
    }
  };

  const deleteExpenseType = async item => {
    if (!window.confirm(`Delete expense type "${item.name}"?`)) return;

    try {
      const res = await apiFetch(`/api/purchase/expense-types/${item.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      setExpenseTypes(prev => prev.filter(row => row.id !== item.id));
    } catch (err) {
      console.error("Failed to delete expense type", err);
      alert("Could not delete expense type");
    }
  };

  const parseCSV = (csvText) => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) {
      throw new Error("CSV file must contain headers and at least one row of data");
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim() === '') continue;
      
      const values = lines[i].split(',').map(v => v.trim());
      const row = {};

      headers.forEach((header, idx) => {
        row[header] = values[idx] || '';
      });

      data.push(row);
    }

    return data;
  };

  const validateAndFormatService = (csvRow) => {
    const getFirstValue = (row, keys) => {
      for (const key of keys) {
        const value = row[key];
        if (value !== undefined && String(value).trim() !== "") {
          return String(value).trim();
        }
      }
      return "";
    };

    if (!csvRow.name) {
      throw new Error("Service name is required");
    }

    const priceRaw = getFirstValue(csvRow, ["price"]);
    const price = Number.parseFloat(priceRaw);

    const durationRaw = getFirstValue(csvRow, [
      "durationminutes",
      "duration_minutes",
      "duration",
      "durationmins",
      "durationinutes"
    ]);
    const durationMinutes = Number.parseInt(durationRaw, 10);

    return {
      name: csvRow.name,
      price: Number.isFinite(price) ? price : 0,
      priceType: getFirstValue(csvRow, ["pricetype", "price_type"]) || "fixed",
      durationMinutes: Number.isFinite(durationMinutes) ? durationMinutes : 30,
      category: getFirstValue(csvRow, ["category"]),
      treatmentType: getFirstValue(csvRow, ["treatmenttype", "treatment_type"]),
      note: getFirstValue(csvRow, ["note"])
    };
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCsvUploadLoading(true);
    setCsvUploadMessage("");

    try {
      const fileContent = await file.text();
      const rows = parseCSV(fileContent);
      
      if (rows.length === 0) {
        throw new Error("No valid service data found in CSV");
      }

      const services = rows.map(validateAndFormatService);
      
      let successCount = 0;
      let failedServices = [];

      for (const service of services) {
        try {
          const res = await apiFetch("/api/services", {
            method: "POST",
            body: JSON.stringify(service)
          });
          
          if (!res.ok) {
            failedServices.push(`${service.name}: Server error ${res.status}`);
          } else {
            successCount++;
          }
        } catch (err) {
          failedServices.push(`${service.name}: ${err.message}`);
        }
      }

      let message = `Successfully imported ${successCount} service(s)`;
      if (failedServices.length > 0) {
        message += ` (Failed: ${failedServices.length})`;
      }

      setCsvUploadMessage(message);
      
      // Reload services page data
      setTimeout(() => {
        loadMasterData();
      }, 1000);

    } catch (err) {
      console.error("CSV upload error:", err);
      setCsvUploadMessage(`Error: ${err.message}`);
    } finally {
      setCsvUploadLoading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="settings-page page main-content">
      <div className="settings-header">
        <h1>Settings</h1>
        <p>Manage master data for service creation.</p>
      </div>

      <div className="settings-grid">
        <section className="settings-card">
          <h2>Upload Services from CSV</h2>
          <div className="csv-upload-section">
            <div className="csv-help-text">
              <p><strong>CSV Format:</strong></p>
              <p style={{fontSize: "0.85rem", color: "#666"}}>
                Your CSV file should have headers: <code>name, price, priceType, durationMinutes, category, treatmentType, note</code>
              </p>
              <p style={{fontSize: "0.85rem", color: "#666", marginTop: "8px"}}>
                <strong>Example:</strong><br/>
                name,price,priceType,durationMinutes,category,treatmentType,note<br/>
                Haircut,500,fixed,30,Hair,Cutting,<br/>
                Massage,1000,from,60,Therapy,Wellness,Full body massage
              </p>
              <p style={{fontSize: "0.85rem", marginTop: "10px"}}>
                <a href="/services_template.csv" download style={{color: "var(--accent)", textDecoration: "none", fontWeight: "500"}}>📥 Download Sample CSV</a>
              </p>
            </div>
            <label className="csv-upload-label">
              <input
                type="file"
                accept=".csv"
                onChange={handleCSVUpload}
                disabled={csvUploadLoading}
                style={{ display: "none" }}
              />
              <span className="csv-upload-btn">
                {csvUploadLoading ? "Uploading..." : "Choose CSV File"}
              </span>
            </label>
            {csvUploadMessage && (
              <div className={`csv-upload-message ${csvUploadMessage.includes("Successfully") ? "success" : "error"}`}>
                {csvUploadMessage}
              </div>
            )}
          </div>
        </section>

        <section className="settings-card">
          <h2>Service Category Master</h2>
          <form className="settings-master-form" onSubmit={addCategory}>
            <div className="settings-master-row">
              <input
                type="text"
                placeholder="Add service category"
                value={newCategoryName}
                onChange={e => setNewCategoryName(e.target.value)}
              />
              <button type="submit" disabled={isSavingCategory}>{isSavingCategory ? "Adding..." : "Add"}</button>
            </div>
          </form>
          <div className="settings-chip-list">
            {categories.map(item => (
              <span className="settings-chip" key={item.id}>
                {item.name}
                <button type="button" onClick={() => deleteCategory(item)} aria-label={`Delete ${item.name}`}>x</button>
              </span>
            ))}
          </div>
        </section>

        <section className="settings-card">
          <h2>Treatment Type Master</h2>
          <form className="settings-master-form" onSubmit={addTreatmentType}>
            <div className="settings-master-row">
              <input
                type="text"
                placeholder="Add treatment type"
                value={newTreatmentTypeName}
                onChange={e => setNewTreatmentTypeName(e.target.value)}
              />
              <button type="submit" disabled={isSavingTreatment}>{isSavingTreatment ? "Adding..." : "Add"}</button>
            </div>
          </form>
          <div className="settings-chip-list">
            {treatmentTypes.map(item => (
              <span className="settings-chip" key={item.id}>
                {item.name}
                <button type="button" onClick={() => deleteTreatmentType(item)} aria-label={`Delete ${item.name}`}>x</button>
              </span>
            ))}
          </div>
        </section>

        <section className="settings-card">
          <h2>Expense Type Master</h2>
          <form className="settings-master-form" onSubmit={addExpenseType}>
            <div className="settings-master-row">
              <input
                type="text"
                placeholder="Add expense type"
                value={newExpenseTypeName}
                onChange={e => setNewExpenseTypeName(e.target.value)}
              />
              <button type="submit" disabled={isSavingExpenseType}>{isSavingExpenseType ? "Adding..." : "Add"}</button>
            </div>
          </form>
          <div className="settings-chip-list">
            {expenseTypes.map(item => (
              <span className="settings-chip" key={item.id}>
                {item.name}
                <button type="button" onClick={() => deleteExpenseType(item)} aria-label={`Delete ${item.name}`}>x</button>
              </span>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Settings;
